import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from '../../utils/toast';
import {
  AlertTriangle,
  Save,
  ArrowLeft,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import OffboardingStepSkeleton from './OffboardingStepSkeleton';
import HorizontalStepper from '../../components/UI/HorizontalStepper';
import { StatusBadge } from '../../components/UI/StatusBadge';
import WithdrawalModal from '../../components/WithdrawalModal';
import useWithdrawal from '../../hooks/useWithdrawal';
import useOffboardingWorkflow from '../../hooks/useOffboardingWorkflow';
import FormSelect from '../../components/UI/FormSelect';
import { getOffboardingSteps, calculateProgressPercentage } from '../../config/offboardingSteps';
import { initializeStepData } from '../../utils/workflowUtils';


const OffboardingStep4 = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { handleWithdrawal } = useWithdrawal();
  const { workflow, loading, saving: hookSaving, saveStep } = useOffboardingWorkflow(workflowId);
  const [saving, setSaving] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [stepData, setStepData] = useState({
    active_bonds_detected: false,
    bond_breach_amount: '',
    bond_settlement_method: '',
    bond_calculation_details: ''
  });

  // Handle step click navigation
  const handleStepClick = (step) => {
    navigate(step.route);
  };

  // Initialize step data when workflow loads
  useEffect(() => {
    if (workflow) {
      const initialData = initializeStepData(workflow, 4);
      setStepData(initialData);
    }
  }, [workflow]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveStep(4, stepData);
    } catch (error) {
      console.error('Error saving step:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-5`);
  };

  const handlePrev = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-3`);
  };

  if (loading) {
    return <OffboardingStepSkeleton />;
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Workflow Not Found</h2>
          <p className="text-gray-600">The requested offboarding workflow could not be found.</p>
        </div>
      </div>
    );
  }

  const steps = getOffboardingSteps(workflow, workflowId);
  const progressPercentage = calculateProgressPercentage(workflow);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white">
        <div className="px-6 py-8 space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                Step 4: Bond Assessment
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                {workflow.employee.first_name} {workflow.employee.last_name} • {workflow.employee.staff_id}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">Step 4 of {workflow?.requires_ir21 ? 7 : 6}</span>
              {workflow.step_4_completed && (
                <StatusBadge status="completed" showIcon />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Horizontal Stepper */}
      <HorizontalStepper
        steps={steps}
        currentStep={4}
        onStepClick={handleStepClick}
        progressPercentage={progressPercentage}
      />

      {/* Main Content */}
      <div className="px-6 py-8 space-y-8">
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          <div className="flex items-center mb-4 sm:mb-6 min-w-0">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Sponsorship Bond Check</h2>
          </div>

          <div className="mb-6">
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={stepData.active_bonds_detected}
                onChange={(e) => setStepData({...stepData, active_bonds_detected: e.target.checked})}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_4_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Active bonds detected for this employee</span>
            </label>
          </div>

          {stepData.active_bonds_detected && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bond Breach Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={stepData.bond_breach_amount}
                  onChange={(e) => setStepData({...stepData, bond_breach_amount: e.target.value})}
                  className="input-admin"
                  placeholder="0.00"
                  disabled={workflow.step_4_completed}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bond Settlement Method
                </label>
                <FormSelect
                  options={[
                    { value: '', label: 'Select method...' },
                    { value: 'full_payment', label: 'Full Payment' },
                    { value: 'salary_deduction', label: 'Salary Deduction' },
                    { value: 'installment', label: 'Installment Plan' },
                    { value: 'waived', label: 'Waived' },
                  ]}
                  value={stepData.bond_settlement_method}
                  onChange={(val) => setStepData({...stepData, bond_settlement_method: val})}
                  theme="admin"
                  disabled={workflow.step_4_completed}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bond Calculation Details
                </label>
                <textarea
                  value={stepData.bond_calculation_details}
                  onChange={(e) => setStepData({...stepData, bond_calculation_details: e.target.value})}
                  rows={4}
                  className="textarea-admin"
                  placeholder="Detailed breakdown of bond calculations..."
                  disabled={workflow.step_4_completed}
                />
              </div>
            </div>
          )}

          {!stepData.active_bonds_detected && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex items-center text-green-800">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">No active bonds detected</span>
              </div>
              <p className="text-green-700 text-sm mt-1">
                This employee has no outstanding sponsorship bonds that require settlement.
              </p>
            </div>
          )}

          {!workflow.step_4_completed && workflow.workflow_status !== 'withdrawn' && (
            <div className="mt-8 flex justify-between">
              <button
                onClick={handlePrev}
                className="btn btn-admin-outline"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous Step
              </button>

              <div className="flex space-x-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-admin-secondary"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Progress'}
                </button>

                {!workflow.step_4_completed && workflow.workflow_status !== 'withdrawn' && (
                  <button
                    onClick={() => setShowWithdrawalModal(true)}
                    className="btn btn-danger"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Withdraw
                  </button>
                )}

                <button
                  onClick={handleNext}
                  className="btn btn-admin-primary"
                >
                  Next Step
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          )}

          {workflow.step_4_completed && (
            <div className="mt-8 flex justify-between">
              <button
                onClick={handlePrev}
                className="btn btn-admin-outline"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous Step
              </button>

              <button
                onClick={handleNext}
                className="btn btn-admin-primary"
              >
                Next Step
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          )}
        </div>

        {/* Withdrawal Modal */}
        <WithdrawalModal
          isOpen={showWithdrawalModal}
          onClose={() => setShowWithdrawalModal(false)}
          currentStep={4}
          onConfirmWithdraw={handleWithdrawal}
          workflow={workflow}
        />
        </div>
      </div>
  );
};

export default OffboardingStep4;
